<!--
  Composer — Phase 4-3 / T15.

  Auto-sizing textarea + Send button, wired to `sendMessageMutation` for
  optimistic insert into the messages cache. Enter sends; Shift+Enter inserts
  newline. Failed-send shows an inline strip above the input.
-->
<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import {
    sendMessageMutation,
    sendSenderAction,
    uploadAttachment,
    IMAGE_MAX_BYTES,
    maxBytesForFile,
    type SenderAction,
  } from '../lib/api/mutations.js';
  import { sendRealtimeFrame } from '../lib/realtime/sender-singleton.js';
  import { customerProfile, quickReplies } from '../lib/api/queries.js';
  import { http } from '../lib/api/http.js';
  import {
    applyTemplate,
    detectSlash,
    filterTemplates,
    type QuickReplyForMatch,
  } from '../lib/quick-replies/match.js';
  import { renderTemplate, type TemplateContext } from '../lib/quick-replies/template.js';
  import { session } from '../lib/stores/session.svelte.js';

  type Props = {
    conversationId: string;
    // Optional — set for WIDGET conversations so typing flows via the
    // realtime gateway to visitor:<visitorId> instead of through the
    // Meta send-action REST path (which no-ops for WIDGET since
    // there's no externalPageId to send to).
    channelType?: string | null;
    visitorId?: string | null;
  };
  let { conversationId, channelType = null, visitorId = null }: Props = $props();

  const queryClient = useQueryClient();
  const mutation = $derived(createMutation(sendMessageMutation(queryClient, conversationId)));

  // Tenant-shared quick replies. Tied to the operator's tenant, cached
  // 5 min. The picker only renders when the input is in slash-mode so
  // the query is cheap even if the operator never opens it.
  const tenantId = $derived(session.state.sso?.tenant.id ?? null);
  const quickRepliesQuery = $derived(createQuery(quickReplies(tenantId)));
  const templates = $derived<QuickReplyForMatch[]>($quickRepliesQuery.data ?? []);

  // Customer profile drives template variable substitution. Already
  // cached by CustomerContextPanel so this is a free subscription —
  // the second consumer hits the warm TanStack cache.
  const customerQuery = $derived(createQuery(customerProfile(conversationId)));
  const templateContext = $derived<TemplateContext>({
    customer: $customerQuery.data
      ? {
          firstName: $customerQuery.data.firstName,
          lastName: $customerQuery.data.lastName,
          displayName: $customerQuery.data.displayName,
        }
      : null,
    operator: session.state.sso?.user
      ? {
          firstName: parseFirstName(session.state.sso.user.displayName ?? null),
          lastName: parseLastName(session.state.sso.user.displayName ?? null),
          displayName: session.state.sso.user.displayName ?? null,
        }
      : null,
    tenant: session.state.sso?.tenant
      ? { name: session.state.sso.tenant.name ?? null }
      : null,
  });

  function parseFirstName(displayName: string | null): string | null {
    if (!displayName) return null;
    const trimmed = displayName.trim();
    if (trimmed.length === 0) return null;
    const space = trimmed.indexOf(' ');
    return space === -1 ? trimmed : trimmed.slice(0, space);
  }

  function parseLastName(displayName: string | null): string | null {
    if (!displayName) return null;
    const space = displayName.trim().indexOf(' ');
    if (space === -1) return null;
    return displayName.trim().slice(space + 1).trim() || null;
  }

  let value = $state('');
  let textareaEl: HTMLTextAreaElement | null = $state(null);
  let errorVisible = $state(false);
  let errorMessage = $state('');
  let aiDrafting = $state(false);
  // M4.1 — image-send state. `selectedImage` holds the picked File until send;
  // `imagePreviewUrl` is a local object URL for instant preview (revoked on
  // clear/send); `uploading` gates the composer during the PUT to storage.
  let fileInputEl: HTMLInputElement | null = $state(null);
  let selectedImage: File | null = $state(null);
  let imagePreviewUrl: string | null = $state(null);
  /** Derived, not a second flag: two booleans about one file drift apart. */
  // Through a function, not `selectedImage?.type`: at the point of
  // declaration the compiler has narrowed the state variable to its `null`
  // initialiser, and the optional chain then types as `never`.
  const isVideoFile = (f: File | null): boolean => !!f && f.type.startsWith('video/');
  const selectedIsVideo = $derived(isVideoFile(selectedImage));
  let uploading = $state(false);
  // Phase 2b: when ticked, the next send carries a user_phone_number Quick
  // Reply chip. Auto-unsets after one send so a stray double-send doesn't
  // re-ask. Operator can re-tick if they need another ask.
  let requestPhoneNumber = $state(false);

  // Slash-picker state. Derived from `value` rather than tracked
  // imperatively so a programmatic reset (template applied, send) just
  // re-runs the derivation and closes the picker.
  const slash = $derived(detectSlash(value));
  const matches = $derived(slash ? filterTemplates(templates, slash.query) : []);
  const pickerOpen = $derived(slash !== null && matches.length > 0);
  let highlightedIndex = $state(0);

  // Clamp the highlight whenever the match list shrinks (e.g. the
  // operator typed another char and only 1 row matches now).
  $effect(() => {
    if (matches.length === 0) {
      highlightedIndex = 0;
    } else if (highlightedIndex >= matches.length) {
      highlightedIndex = matches.length - 1;
    }
  });

  // --- Typing indicator (Phase 1) ----------------------------------------
  // Fire `typing_on` on first keystroke, re-send every TYPING_RESEND_MS
  // while still typing (Meta auto-times-out after ~10 s), and fire
  // `typing_off` after TYPING_IDLE_MS of inactivity or on send/blur.
  const TYPING_RESEND_MS = 6000;
  const TYPING_IDLE_MS = 6000;
  let typingActive = false;
  let typingResendTimer: ReturnType<typeof setTimeout> | null = null;
  let typingIdleTimer: ReturnType<typeof setTimeout> | null = null;

  function fireTyping(action: SenderAction): void {
    // WIDGET path: send WS frame so the gateway can fan to the
    // visitor:<visitorId> room. Skips the Meta REST hop entirely
    // (the visitor isn't on Meta; channels-meta has nothing to do).
    if (channelType === 'WIDGET' && visitorId) {
      sendRealtimeFrame({
        type: 'conversation.typing',
        conversationId,
        isTyping: action === 'typing_on',
        visitorId,
      });
      return;
    }
    void sendSenderAction(conversationId, action);
  }

  function clearTypingTimers(): void {
    if (typingResendTimer !== null) {
      clearTimeout(typingResendTimer);
      typingResendTimer = null;
    }
    if (typingIdleTimer !== null) {
      clearTimeout(typingIdleTimer);
      typingIdleTimer = null;
    }
  }

  function stopTyping(): void {
    clearTypingTimers();
    if (typingActive) {
      typingActive = false;
      fireTyping('typing_off');
    }
  }

  function noteKeystroke(): void {
    if (!typingActive) {
      typingActive = true;
      fireTyping('typing_on');
    }
    clearTypingTimers();
    typingResendTimer = setTimeout(() => {
      if (typingActive) fireTyping('typing_on');
    }, TYPING_RESEND_MS);
    typingIdleTimer = setTimeout(() => {
      stopTyping();
    }, TYPING_IDLE_MS);
  }

  const trimmed = $derived(value.trim());
  const isPending = $derived($mutation.isPending);
  // M4.1 — sendable when there's text OR a picked image; blocked while a
  // send is in flight or an image is uploading.
  const sendDisabled = $derived(
    (trimmed.length === 0 && !selectedImage) || isPending || uploading,
  );

  function clearImage() {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      imagePreviewUrl = null;
    }
    selectedImage = null;
    if (fileInputEl) fileInputEl.value = '';
  }

  function handleImagePick(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    errorVisible = false;
    errorMessage = '';
    // The picker still offers images only — the video half of this control is
    // M4.8. The CHECK is already per-kind so that the day `accept` widens,
    // the message does not have to be found and fixed separately.
    const maxBytes = maxBytesForFile(file.type);
    if (maxBytes === null) {
      errorVisible = true;
      errorMessage =
        'Unsupported file type. Use a JPEG, PNG, GIF or WebP image, or an MP4, MOV, WebM or 3GP video.';
      input.value = '';
      return;
    }
    if (file.size > maxBytes) {
      errorVisible = true;
      const kind = maxBytes === IMAGE_MAX_BYTES ? 'Image' : 'Video';
      errorMessage = `${kind} is too large (max ${Math.floor(maxBytes / (1024 * 1024))} MB).`;
      input.value = '';
      return;
    }
    // Replace any prior pick — revoke the old preview before creating a new one.
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    selectedImage = file;
    imagePreviewUrl = URL.createObjectURL(file);
  }

  function autoresize() {
    const el = textareaEl;
    if (!el) return;
    el.style.height = 'auto';
    const max = 138; // ~6 lines at line-height 1.45, 8px vert padding
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  }

  function generateIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  async function submit() {
    if (sendDisabled) return;
    errorVisible = false;
    errorMessage = '';
    // Pop the typing indicator before the message lands so the customer
    // doesn't see "still typing" alongside the new message.
    stopTyping();

    // M4.1 — image send takes precedence over text: upload the file to object
    // storage (presigned PUT), then send the message referencing the public
    // URL. Text in the box is preserved (not sent with the image — Messenger
    // can't combine them in v1).
    if (selectedImage) {
      const file = selectedImage;
      const preview = imagePreviewUrl;
      const clientId = generateIdempotencyKey();
      uploading = true;
      try {
        const attachmentUrl = await uploadAttachment(conversationId, file);
        await $mutation.mutateAsync({
          text: '',
          clientId,
          attachmentUrl,
          // The kind travels with the send. The server cannot recover it —
          // our object keys carry no extension — and it is what tells Meta to
          // render a player rather than a photo.
          attachmentKind: selectedIsVideo ? 'video' : 'image',
          ...(preview ? { previewUrl: preview } : {}),
        });
        // Clear the picker. Defer revoking the object URL: the optimistic
        // bubble renders `preview` until the post-send refetch swaps in the
        // server's public URL; revoking now would break that preview.
        selectedImage = null;
        imagePreviewUrl = null;
        if (fileInputEl) fileInputEl.value = '';
        if (preview) setTimeout(() => URL.revokeObjectURL(preview), 30_000);
      } catch (err) {
        errorVisible = true;
        errorMessage =
          err instanceof Error ? err.message : 'Could not send the file. Try again.';
      } finally {
        uploading = false;
      }
      return;
    }

    const text = trimmed;
    const askPhone = requestPhoneNumber;
    try {
      await $mutation.mutateAsync({
        text,
        clientId: generateIdempotencyKey(),
        ...(askPhone ? { requestPhoneNumber: true } : {}),
      });
      value = '';
      requestPhoneNumber = false;
      // Reset textarea height after clearing
      queueMicrotask(autoresize);
    } catch (err) {
      errorVisible = true;
      errorMessage =
        err instanceof Error ? err.message : 'Could not send. Check your connection and try again.';
    }
  }

  function applyHighlighted(): boolean {
    if (!pickerOpen) return false;
    const pick = matches[highlightedIndex];
    if (!pick) return false;
    const rendered = renderTemplate(pick.body, templateContext);
    const { nextValue, caret } = applyTemplate(rendered);
    value = nextValue;
    // Defer caret + autoresize until after Svelte writes the value back
    // to the textarea, otherwise we'd set the caret on the pre-update
    // DOM and Svelte would clobber it on the next tick.
    queueMicrotask(() => {
      const el = textareaEl;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
      autoresize();
    });
    highlightedIndex = 0;
    return true;
  }

  function handleKeydown(event: KeyboardEvent) {
    // Picker-aware keys take precedence so the textarea doesn't
    // intercept Enter / arrows while the popover is open.
    if (pickerOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlightedIndex = (highlightedIndex + 1) % matches.length;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlightedIndex = (highlightedIndex - 1 + matches.length) % matches.length;
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        // Strip the leading `/` so the picker closes but the operator
        // isn't punished if they typed real content after `/`.
        value = '';
        highlightedIndex = 0;
        queueMicrotask(autoresize);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (applyHighlighted()) {
          event.preventDefault();
          return;
        }
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  }

  function handleInput(event: Event) {
    value = (event.currentTarget as HTMLTextAreaElement).value;
    autoresize();
    if (value.trim().length > 0) {
      noteKeystroke();
    } else {
      // Cleared to empty — no point telling FB we're still typing.
      stopTyping();
    }
  }

  function pickByClick(index: number) {
    highlightedIndex = index;
    applyHighlighted();
  }

  /**
   * Ask the server for an AI-generated draft based on conversation
   * history, populate the textarea, and let the operator edit before
   * sending. Server gates on the per-tenant aiDraftEnabled flag → 403
   * (or 409 if no provider configured) surfaces as an inline error
   * strip without breaking the rest of the composer.
   */
  async function requestAiDraft() {
    if (aiDrafting || isPending) return;
    aiDrafting = true;
    errorVisible = false;
    errorMessage = '';
    try {
      const res = await http.post<{ draft: string; provider: string; model: string }>(
        `/api/conversations/${conversationId}/ai-draft`,
        {},
      );
      value = res.draft;
      queueMicrotask(() => {
        const el = textareaEl;
        if (el) {
          el.focus();
          el.setSelectionRange(value.length, value.length);
        }
        autoresize();
      });
    } catch (err) {
      errorVisible = true;
      errorMessage =
        err instanceof Error
          ? err.message
          : 'Could not generate a draft. Try again or compose manually.';
    } finally {
      aiDrafting = false;
    }
  }

  function handleBlur() {
    stopTyping();
  }

  function dismissError() {
    errorVisible = false;
  }
</script>

<form
  class="composer"
  onsubmit={(e) => {
    e.preventDefault();
    void submit();
  }}
>
  {#if errorVisible}
    <div class="error" role="alert">
      <span class="error-text">{errorMessage || 'Could not send. Check your connection and try again.'}</span>
      <button type="button" class="dismiss" onclick={dismissError}>Dismiss</button>
    </div>
  {/if}

  {#if pickerOpen}
    <div
      class="picker"
      role="listbox"
      aria-label="Quick replies"
      id="quick-reply-picker"
    >
      {#each matches as match, i (match.slug)}
        <button
          type="button"
          class="picker-row"
          class:active={i === highlightedIndex}
          role="option"
          aria-selected={i === highlightedIndex}
          onclick={() => pickByClick(i)}
          onmouseenter={() => (highlightedIndex = i)}
          tabindex="-1"
        >
          <span class="picker-slug">/{match.slug}</span>
          <span class="picker-name">{match.name}</span>
          <span class="picker-body">{match.body}</span>
        </button>
      {/each}
      <div class="picker-hint">
        ↑↓ to navigate · Enter or Tab to insert · Esc to cancel
      </div>
    </div>
  {/if}

  {#if imagePreviewUrl}
    <div class="image-preview">
      {#if selectedIsVideo}
        <!-- `muted` so a pick never makes noise, and no `controls`: this is a
             thumbnail of what is about to be sent, not a player. -->
        <video src={imagePreviewUrl} muted playsinline aria-label="Selected video preview"></video>
      {:else}
        <img src={imagePreviewUrl} alt="Selected attachment preview" />
      {/if}
      <button
        type="button"
        class="image-remove"
        onclick={clearImage}
        disabled={uploading}
        aria-label="Remove selected file"
      >✕</button>
      {#if uploading}<span class="image-uploading">Uploading…</span>{/if}
    </div>
  {/if}

  <input
    bind:this={fileInputEl}
    type="file"
    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/3gpp"
    class="file-input"
    onchange={handleImagePick}
    aria-hidden="true"
    tabindex="-1"
  />

  <div class="grid">
    <textarea
      bind:this={textareaEl}
      class="input"
      rows="1"
      placeholder="Write a reply"
      value={value}
      oninput={handleInput}
      onkeydown={handleKeydown}
      onblur={handleBlur}
      aria-label="Message body"
      aria-autocomplete="list"
      aria-controls={pickerOpen ? 'quick-reply-picker' : undefined}
      disabled={isPending || uploading}
    ></textarea>
    <button
      type="submit"
      class="send"
      disabled={sendDisabled}
      aria-label={uploading ? 'Uploading image' : isPending ? 'Sending message' : 'Send message'}
    >
      {uploading ? 'Uploading' : isPending ? 'Sending' : 'Send'}<span class="glyph" aria-hidden="true">↗</span>
    </button>
  </div>

  <div class="composer-extras">
    <button
      type="button"
      class="attach"
      onclick={() => fileInputEl?.click()}
      disabled={isPending || uploading}
      title="Attach an image or video"
      aria-label="Attach an image or video"
    >
      <span aria-hidden="true">📎</span>
      <span>Image</span>
    </button>
    <button
      type="button"
      class="ai-draft"
      onclick={requestAiDraft}
      disabled={aiDrafting || isPending}
      title="Generate a draft reply from conversation history"
      aria-label={aiDrafting ? 'Drafting' : 'Generate AI draft'}
    >
      <span aria-hidden="true">✨</span>
      <span>{aiDrafting ? 'Drafting…' : 'Draft with AI'}</span>
    </button>
    <label class="ask-phone">
      <input
        type="checkbox"
        bind:checked={requestPhoneNumber}
        disabled={isPending}
      />
      <span>Request phone</span>
    </label>
  </div>
</form>

<style>
  .composer {
    display: flex;
    flex-direction: column;
    padding: 10px 12px 12px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border-top: 1px solid var(--widget-border, oklch(92% 0.005 250));
    position: relative;
  }

  .picker {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: calc(100% - 4px);
    margin-bottom: 6px;
    max-height: 280px;
    overflow-y: auto;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 8px);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);
    z-index: 10;
    padding: 4px 0;
  }
  .picker-row {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 8px;
    row-gap: 2px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    padding: 6px 12px;
    cursor: pointer;
    color: var(--widget-fg, oklch(20% 0.01 250));
    font: inherit;
  }
  .picker-row.active {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 12%, transparent);
  }
  .picker-slug {
    grid-column: 1;
    grid-row: 1;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    white-space: nowrap;
  }
  .picker-name {
    grid-column: 2;
    grid-row: 1;
    font-size: 13px;
    font-weight: 600;
  }
  .picker-body {
    grid-column: 1 / -1;
    grid-row: 2;
    font-size: 12px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .picker-hint {
    padding: 6px 12px;
    border-top: 1px solid var(--widget-border, oklch(92% 0.005 250));
    margin-top: 4px;
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }

  .composer-extras {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
  }
  .ai-draft {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: var(--widget-radius-action, 5px);
    background: transparent;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 11.5px;
    font-weight: 600;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    cursor: pointer;
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .ai-draft:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 8%, transparent);
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .ai-draft:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .attach {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: var(--widget-radius-action, 5px);
    background: transparent;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 11.5px;
    font-weight: 600;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    cursor: pointer;
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .attach:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 8%, transparent);
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .attach:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Visually-hidden native file input — triggered via the .attach button. */
  .file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .image-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .image-preview img,
  .image-preview video {
    max-height: 64px;
    max-width: 120px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
  }
  .image-remove {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
  }
  .image-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .image-uploading {
    font-size: 11.5px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }

  .ask-phone {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    cursor: pointer;
    user-select: none;
  }
  .ask-phone input {
    accent-color: var(--widget-accent, oklch(75% 0.165 75));
    cursor: pointer;
  }
  .ask-phone input:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: end;
  }

  .input {
    width: 100%;
    resize: none;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 8px);
    padding: 8px 12px;
    min-height: 38px;
    max-height: 138px;
    font: inherit;
    font-size: 13.5px;
    line-height: 1.45;
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    transition: border-color 120ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .input::placeholder {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .input:focus {
    outline: 3px solid color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 22%, transparent);
    outline-offset: 0;
    border-color: var(--widget-accent, oklch(75% 0.165 75));
  }
  .input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .send {
    height: 38px;
    padding: 0 14px;
    border-radius: var(--widget-radius-action, 5px);
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.012 250));
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: filter 100ms cubic-bezier(0.22, 1, 0.36, 1);
    white-space: nowrap;
  }
  .send:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .send:active:not(:disabled) {
    filter: brightness(0.94);
  }
  .send:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .send:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: none;
  }
  .glyph {
    font-size: 14px;
    line-height: 1;
  }

  .error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    margin-bottom: 8px;
    border-radius: 8px;
    background: color-mix(in oklch, oklch(55% 0.18 25) 8%, transparent);
    color: oklch(40% 0.16 25);
    font-size: 12px;
    line-height: 1.3;
  }
  .error-text {
    flex: 1;
  }
  .dismiss {
    background: none;
    border: none;
    color: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }
  .dismiss:hover {
    text-decoration: underline;
  }
</style>
